import {
  useEffect, useState,
} from 'react';
import {
  getPlaceDetails, type PlaceDetail, PlacesSearchProvider,
} from '~/places/places.repository';
import type { PlaceId } from '~/places/places.types';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';

// details are fetched for Foursquare places only, Google ones are to follow
const PROVIDERS_WITH_DETAILS: ReadonlyArray<PlacesSearchProvider> = [PlacesSearchProvider.Foursquare];

export const usePlaceDetails = ({ placeId, provider }: {
  placeId: PlaceId | undefined;
  provider: PlacesSearchProvider | undefined;
}): Readonly<{
  placeDetail: PlaceDetail | null;
  isLoading: boolean;
}> => {
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const isMapPresentational = useIsMapPresentationalSelector();
  const [placeDetail, setPlaceDetail] = useState<PlaceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setPlaceDetail(null);

    if (!clientId || !placeId || !provider || !PROVIDERS_WITH_DETAILS.includes(provider)) {
      return;
    }

    // the response of a place the user already navigated away from is dropped
    let isRequestCurrent = true;
    setIsLoading(true);

    getPlaceDetails(clientId, placeId, provider, isMapPresentational ? (mapId || undefined) : undefined)
      .then(response => {
        // a successful response carries no code, only the data envelope
        if (isRequestCurrent && response.data && !response.code) {
          setPlaceDetail(response.data);
        }
      })
      .catch(console.error)
      .finally(() => {
        if (isRequestCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isRequestCurrent = false;
    };
  }, [clientId, placeId, provider, isMapPresentational, mapId]);

  return { placeDetail, isLoading };
};
